import { useState, useEffect, useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { useLayoutEffect } from "react";
import * as THREE from "three";
import useScreenSize from "../stuff/useScreenSize";
import { View } from "@react-three/drei";
import EmergeMaterial from "../stuff/EmergeMaterial";

const PIXELS = [
  1, 1.5, 2, 2.5, 3, 1, 1.5, 2, 2.5, 3, 3.5, 4, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5,
  6, 3, 3.5, 4, 4.5, 5, 5.5, 6, 6.5, 7, 7.5, 8, 8.5, 9, 20, 100,
].map((v) => v / 100);

export default function EmergingText({
  animate = false,
  type = 0,
  title = "",
  description = "",
}) {
  // Set fillColor to white for the final state
  const fillColor = "#ffffff";

  const [refMesh, setRefMesh] = useState(null);
  const [texture, setTexture] = useState(null);
  const [finalTexture, setFinalTexture] = useState(null);
  const [textureSize, setTextureSize] = useState([1024, 512]);
  const [elementSize, setElementSize] = useState([0, 0]);
  const ref = useRef();
  const screenSize = useScreenSize();

  // Create both the animation texture and final texture
  useEffect(() => {
    // Create animation texture FIRST (with checkered pattern) - this will be our starting point
    const animCanvas = document.createElement("canvas");
    animCanvas.width = 1024;
    animCanvas.height = 512;
    const animCtx = animCanvas.getContext("2d");

    // Create checkered background pattern
    const cellSize = 32; // Size of each cell in the checkered pattern

    for (let y = 0; y < animCanvas.height; y += cellSize) {
      for (let x = 0; x < animCanvas.width; x += cellSize) {
        // Determine which color to use based on position
        const isEvenRow = Math.floor(y / cellSize) % 2 === 0;
        const isEvenCol = Math.floor(x / cellSize) % 2 === 0;

        // Alternate colors: #111111 and #000000
        if ((isEvenRow && isEvenCol) || (!isEvenRow && !isEvenCol)) {
          animCtx.fillStyle = "#111111";
        } else {
          animCtx.fillStyle = "#000000";
        }

        // Draw the cell
        animCtx.fillRect(x, y, cellSize, cellSize);
      }
    }

    // Create the animation texture (checkered pattern)
    const animTexture = new THREE.CanvasTexture(animCanvas);

    // Set the checkered pattern as the initial texture
    setTexture(animTexture);

    // Create final texture (white background with content)
    const finalCanvas = document.createElement("canvas");
    finalCanvas.width = 1024;
    finalCanvas.height = 512;
    const finalCtx = finalCanvas.getContext("2d");

    // White background
    finalCtx.fillStyle = "#ffffff";
    finalCtx.fillRect(0, 0, finalCanvas.width, finalCanvas.height);

    // Create the final texture
    const endTexture = new THREE.CanvasTexture(finalCanvas);
    setFinalTexture(endTexture);
  }, [title, description]);

  // Helper function to wrap text
  function getLines(ctx, text, maxWidth) {
    if (!text) return [];

    const words = text.split(" ");
    const lines = [];
    let currentLine = words[0];

    for (let i = 1; i < words.length; i++) {
      const word = words[i];
      const width = ctx.measureText(currentLine + " " + word).width;
      if (width < maxWidth) {
        currentLine += " " + word;
      } else {
        lines.push(currentLine);
        currentLine = word;
      }
    }
    lines.push(currentLine);
    return lines;
  }

  useEffect(() => {
    if (refMesh) {
      refMesh.material.uProgress = 0;
      refMesh.material.uType = type;
    }
  }, [type]);

  // Animation with GSAP
  useGSAP(() => {
    if (refMesh?.material) {
      // Animate progress of the shader effect
      gsap.to(refMesh.material, {
        uProgress: animate ? 1 : 0,
        duration: 1.5,
        ease: "none",
      });

      // Switch textures at the end of animation
      if (animate && finalTexture) {
        gsap.to(
          {},
          {
            duration: 1.4, // Slightly before the main animation ends
            onComplete: () => {
              refMesh.material.uTexture = finalTexture;
            },
          }
        );
      } else if (!animate && texture) {
        // Switch back to checkerboard texture when resetting
        refMesh.material.uTexture = texture;
      }
    }
  }, [animate, refMesh, texture, finalTexture]);

  // Size updates
  useLayoutEffect(() => {
    if (refMesh && ref.current) {
      let bounds = ref.current.getBoundingClientRect();
      setElementSize([bounds.width, bounds.height]);
      refMesh?.scale.set(bounds.width, bounds.height, 1);
    }
  }, [refMesh]);

  // Resize handling
  useEffect(() => {
    if (ref.current && refMesh) {
      let bounds = ref.current.getBoundingClientRect();
      setElementSize([bounds.width, bounds.height]);
      refMesh?.scale.set(bounds.width, bounds.height, 1);
    }
  }, [screenSize, refMesh]);

  return (
    <View ref={ref} style={{ width: "100%", height: "100%" }}>
      {texture && (
        <mesh ref={setRefMesh}>
          <emergeMaterial
            uFillColor={new THREE.Color(fillColor)}
            transparent={true}
            uTexture={texture}
            uPixels={PIXELS}
            uTextureSize={new THREE.Vector2(textureSize[0], textureSize[1])}
            uElementSize={new THREE.Vector2(elementSize[0], elementSize[1])}
          />
          <planeGeometry args={[1, 1]} />
        </mesh>
      )}
    </View>
  );
}
